import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../authentication/AuthProvider";
import { useContextProvider } from "../components/page-context/ContextProvider";
import routing from "../routing";
import AvatarButton from "../components/button/AvatarButton";
import heroImg from "../assets/LandingPage.png";
import logoImg from "../assets/logo/ArivuEN.png";
import "../style/pages-style/LandingPage.css";
import { Menu, MenuItem, ListItemIcon, Divider, Typography } from "@mui/material";
import { LuSettings, LuLifeBuoy, LuBell, LuLogOut, LuRefreshCw, LuChevronDown } from "react-icons/lu";
import { clearUserCache } from "../utilities/queryClient";

/* ────────────────────────────────────
   Inline SVG icon components
   ──────────────────────────────────── */
const IconBarChart = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" />
    </svg>
);
const IconBuilding = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4" /><path d="M8 6h.01" /><path d="M16 6h.01" /><path d="M12 6h.01" /><path d="M12 10h.01" /><path d="M12 14h.01" /><path d="M16 10h.01" /><path d="M16 14h.01" /><path d="M8 10h.01" /><path d="M8 14h.01" />
    </svg>
);
const IconHandshake = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m11 17 2 2a1 1 0 1 0 3-3" /><path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" /><path d="m21 3 1 11h-2" /><path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" /><path d="M3 4h8" />
    </svg>
);
const IconBox = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" />
    </svg>
);
const IconFileText = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" />
    </svg>
);
const IconLink = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
);
const IconTarget = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
    </svg>
);
const IconZap = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
);
const IconShield = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
);
const IconGlobe = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
);
const IconUsers = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);
const IconBook = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
    </svg>
);
const IconMessageCircle = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
);
const IconCalendar = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="18" x="3" y="4" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);
const IconLeaf = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 20A7 7 0 0 1 9.8 6.9C15.5 4.9 17 3.5 19 2c1 2 2 4.5 2 8 0 5.5-4.78 10-10 10Z" /><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
    </svg>
);
const IconBriefcase = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="20" height="14" x="2" y="7" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
);

/* ────────────────────────────────────
   Feature cards data
   ──────────────────────────────────── */
const FEATURES = [
    { icon: IconBox, title: "Product Footprint", desc: "Calculate the carbon footprint of any product with precision — from raw materials to finished goods." },
    { icon: IconFileText, title: "Digital Product Passport", desc: "Create comprehensive DPPs that capture sustainability data, materials, and lifecycle information for every product." },
    { icon: IconLink, title: "Supply Chain Sharing", desc: "Send digital passports seamlessly across your entire supply chain — from suppliers to end consumers." },
    { icon: IconTarget, title: "Building Footprint", desc: "Assess your project and building footprint with granular accuracy across all construction phases." },
    { icon: IconZap, title: "Real-Time Accuracy", desc: "Leverage automated data collection and AI‑driven calculations for fast, reliable emissions data." },
    { icon: IconShield, title: "Regulatory Ready", desc: "Stay compliant with EU DPP regulations, CBAM, and emerging global sustainability standards." },
];

/* ────────────────────────────────────
   Module cards — only actual navigable modules
   ──────────────────────────────────── */
const getSectorModules = (r, auth) => [
    { icon: IconBox, title: "Products", desc: "Comprehensive lifecycle assessment and carbon footprint calculation for all your products.", route: r.sector.products.dashboard, visibility: auth.isProductEnabled },
    { icon: IconCalendar, title: "Projects", desc: "Track and manage sustainability metrics across all your projects with real-time data.", route: r.sector.projects.dashboard, visibility: auth.isProjectEnabled },
    { icon: IconBuilding, title: "Buildings", desc: "Assess building footprints across all construction phases — from materials to operations.", route: r.sector.buildings.dashboard, visibility: auth.isBuildingEnabled },
    { icon: IconFileText, title: "Digital Passport", desc: "Create, manage, and share DPPs across your value chain.", route: r.digital_passport.dashboard, visibility: auth.isDppEnabled },
].filter(m => m.visibility);

const getOrganizationModules = (r, auth) => [
    { icon: IconLink, title: "Supply Chain", desc: "Map, monitor, and manage your entire supply chain's environmental and social impact.", route: r.organization.supply_chain.dashboard, visibility: auth.isOrganizationEnabled },
    { icon: IconBuilding, title: "Organization", desc: "Track organizational-level sustainability metrics and corporate ESG performance.", route: r.organization.structure.dashboard, visibility: auth.isOrganizationEnabled },
    { icon: IconGlobe, title: "Sustainability", desc: "Comprehensive sustainability management with goal-setting and progress tracking.", route: r.organization.sustainability.net_zero, visibility: auth.isOrganizationEnabled },
    { icon: IconShield, title: "Compliance", desc: "Stay ahead of regulatory requirements with automated compliance tracking and reporting.", route: r.organization.compliance.sbti, visibility: auth.isOrganizationEnabled },
].filter(m => m.visibility);

const getEngagementModules = (r, auth) => [
    { icon: IconUsers, title: "Supplier Directory", desc: "Centralized directory of verified, sustainability-rated suppliers.", route: r.engagement.supplier_directory.dashboard, visibility: auth.isEngagementEnabled },
    { icon: IconBriefcase, title: "Client Directory", desc: "Manage client relationships and track sustainability data sharing across your network.", route: r.engagement.client_directory.dashboard, visibility: auth.isEngagementEnabled },
    { icon: IconBook, title: "B2B Directory", desc: "Discover and connect with businesses committed to sustainable practices.", route: r.engagement.b2b_directory.dashboard, visibility: auth.isEngagementEnabled },
    { icon: IconMessageCircle, title: "Collaboration", desc: "Real-time collaboration tools for joint sustainability initiatives.", route: r.engagement.collaborate.dashboard, visibility: auth.isEngagementEnabled },
].filter(m => m.visibility);

/* ════════════════════════════════════
   Scroll Reveal Hook (IntersectionObserver)
   ════════════════════════════════════ */
function useScrollReveal() {
    const ref = useRef(null);

    useEffect(() => {
        const container = ref.current;
        if (!container) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("lp-visible");
                    }
                });
            },
            { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
        );

        const elements = container.querySelectorAll(".lp-reveal");
        elements.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    return ref;
}

/* ════════════════════════════════════
   LandingPage Component
   ════════════════════════════════════ */
function LandingPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { username, attemptLogout, isEngagementEnabled, isOrganizationEnabled, isSectorEnabled, isProductEnabled, isProjectEnabled, isBuildingEnabled, isDppEnabled } = useAuth();
    const { setLeftSidebarCollapse } = useContextProvider();
    const containerRef = useScrollReveal();

    useEffect(() => {
        setLeftSidebarCollapse(true);
    }, [setLeftSidebarCollapse]);

    const sectorModules = useMemo(() => getSectorModules(routing, { isProductEnabled, isProjectEnabled, isBuildingEnabled, isDppEnabled }), [isProductEnabled, isProjectEnabled, isBuildingEnabled, isDppEnabled]);
    const organizationModules = useMemo(() => getOrganizationModules(routing, { isOrganizationEnabled }), [isOrganizationEnabled]);
    const engagementModules = useMemo(() => getEngagementModules(routing, { isEngagementEnabled }), [isEngagementEnabled]);

    const scrollTo = useCallback((id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const menuOpen = Boolean(menuAnchorEl);

    const handleAvatarClick = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const handleMenuNavigate = (path, state = {}) => {
        localStorage.setItem("previousLocationBeforeProfile", location.pathname);
        navigate(path, { state });
        handleMenuClose();
    };

    return (
        <div className="landing-page" ref={containerRef}>
            {/* ── HEADER ── */}
            <header className="lp-header">
                <div /> {/* Spacer to keep nav centered and actions right-aligned */}
                <nav className="lp-header-nav center-nav">
                    {isSectorEnabled && (
                        <a onClick={() => scrollTo("sector")}>
                            <IconBarChart /> {t("Sector")}
                        </a>
                    )}
                    {isOrganizationEnabled && (
                        <a onClick={() => scrollTo("organization")}>
                            <IconBuilding /> {t("Organization")}
                        </a>
                    )}
                    {isEngagementEnabled && (
                        <a onClick={() => scrollTo("engagement")}>
                            <IconHandshake /> {t("Engagement")}
                        </a>
                    )}
                </nav>

                <div className="lp-header-actions">
                    <button className="lp-header-btn" onClick={() => scrollTo("modules")}>
                        {t("Get Started")}
                    </button>
                    <div
                        onClick={handleAvatarClick}
                        style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", padding: "6px 12px", borderRadius: "12px", transition: "background 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(0,0,0,0.04)"}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                        <div style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: "#659d33", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 600, color: "#fff" }}>
                            {username ? username.charAt(0).toUpperCase() : "U"}
                        </div>
                        <LuChevronDown
                            size={18}
                            color="#64748b"
                            style={{
                                transition: "transform 0.3s ease",
                                transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)"
                            }}
                        />
                    </div>
                </div>

                <Menu
                    anchorEl={menuAnchorEl}
                    open={menuOpen}
                    onClose={handleMenuClose}
                    onClick={handleMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    transitionDuration={200}
                    PaperProps={{
                        elevation: 0,
                        sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 8px 16px rgba(0,0,0,0.1))',
                            mt: 1.5,
                            ml: 2,
                            minWidth: 220,
                            borderRadius: "14px",
                            border: "1px solid rgba(0,0,0,0.05)",
                            p: 1,
                            '&:before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                right: 32,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0,
                            },
                        },
                    }}
                >
                    <MenuItem onClick={() => handleMenuNavigate(routing.profile.account_settings, { activeTab: 0 })} sx={{ py: 1.2, px: 2, borderRadius: "10px" }}>
                        <ListItemIcon><LuSettings size={18} /></ListItemIcon>
                        <Typography variant="body2" fontWeight={600}>{t("Settings")}</Typography>
                    </MenuItem>

                    <MenuItem onClick={() => handleMenuNavigate(routing.support.dashboard)} sx={{ py: 1.2, px: 2, borderRadius: "10px" }}>
                        <ListItemIcon><LuLifeBuoy size={18} /></ListItemIcon>
                        <Typography variant="body2" fontWeight={600}>{t("Support")}</Typography>
                    </MenuItem>

                    <MenuItem onClick={() => handleMenuNavigate(routing.profile.account_settings, { activeTab: 3 })} sx={{ py: 1.2, px: 2, borderRadius: "10px" }}>
                        <ListItemIcon><LuBell size={18} /></ListItemIcon>
                        <Typography variant="body2" fontWeight={600}>{t("Notifications")}</Typography>
                    </MenuItem>

                    <MenuItem
                        onClick={async () => {
                            handleMenuClose();
                            await clearUserCache();
                            window.location.reload();
                        }}
                        sx={{ py: 1.2, px: 2, borderRadius: "10px" }}
                    >
                        <ListItemIcon><LuRefreshCw size={18} /></ListItemIcon>
                        <Typography variant="body2" fontWeight={600}>{t("System Refresh")}</Typography>
                    </MenuItem>

                    <Divider sx={{ my: 1, opacity: 0.4 }} />

                    <MenuItem
                        onClick={() => { handleMenuClose(); attemptLogout(); }}
                        sx={{
                            py: 1.2, px: 2, color: "#659d33", borderRadius: "10px", mt: 0.5,
                            "&:hover": { bgcolor: "rgba(101, 157, 51, 0.08)", transform: "translateX(4px)" },
                            transition: "all 0.2s"
                        }}
                    >
                        <ListItemIcon><LuLogOut size={18} style={{ color: "#659d33" }} /></ListItemIcon>
                        <Typography variant="body2" fontWeight={800}>{t("Sign Out")}</Typography>
                    </MenuItem>
                </Menu>

            </header>

            {/* ── HERO ── */}
            <section className="lp-hero" id="hero">
                <div className="lp-hero-content lp-reveal">
                    <div className="lp-hero-badge">🌱 {t("Sustainability Intelligence Platform")}</div>
                    <h1>
                        {t("Democratizing")}{" "}
                        <span className="highlight">{t("Sustainable")}</span>{" "}
                        {t("Transformation Across Your Ecosystem")}
                    </h1>
                    <p className="lp-hero-subtitle">
                        {t("Arivu makes sustainability accessible for every organization")}
                    </p>
                    <div className="lp-hero-buttons">
                        <button className="lp-cta-btn" onClick={() => scrollTo("modules")}>
                            {t("Explore Modules")}
                        </button>
                        <button className="lp-cta-btn-outline" onClick={() => scrollTo("about")}>
                            {t("Learn More")}
                        </button>
                    </div>
                </div>
                <div className="lp-hero-image lp-reveal lp-reveal-delay-2">
                    <img src={heroImg} alt="Sustainability illustration" />
                </div>
            </section>

            {/* ── ABOUT ── */}
            <section className="lp-about" id="about">
                <div className="lp-reveal">
                    <h2 className="lp-section-title">
                        {t("About")} <span className="highlight">Arivu</span>
                    </h2>
                    <p className="lp-section-subtitle">
                        {t("Arivu is a modular sustainability management platform")}
                    </p>
                </div>
            </section>

            {/* ── FEATURES GRID ── */}
            <section className="lp-features">
                <div className="lp-features-header lp-reveal">
                    <div className="lp-why-arivu-badge">{t("Why Arivu")}</div>
                    <h2 className="lp-section-title">
                        {t("Determine Your Product & Building Footprint with Ease, Efficiency & Accuracy")}
                    </h2>
                    <p className="lp-section-subtitle">
                        {t("From cradle-to-gate carbon calculations to Digital Product Passports")}
                    </p>
                </div>
                <div className="lp-features-grid">
                    {FEATURES.map((f, i) => (
                        <div className={`lp-feature-card lp-reveal lp-reveal-delay-${i + 1}`} key={f.title}>
                            <div className="lp-feature-icon"><f.icon /></div>
                            <h3>{t(f.title)}</h3>
                            <p>{t(f.desc)}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── GREEN BANNER ── */}
            <section className="lp-banner-container lp-reveal">
                <div className="lp-banner-card">
                    <div className="lp-banner-content">
                        <h2>{t("Digital Product Passports, Simplified")}</h2>
                        <p>
                            {t("Create, manage, and share DPPs across your value chain in minutes.")}
                        </p>
                    </div>
                    <div className="lp-banner-actions">
                        <button className="lp-banner-btn" onClick={() => navigate(routing.sector.products.dashboard)}>
                            {t("See How It Works")}
                        </button>
                    </div>
                </div>
            </section>

            {/* ── PLATFORM MODULES ── */}
            <section className="lp-modules" id="modules">
                <div className="lp-modules-header lp-reveal">
                    <h2 className="lp-section-title">
                        {t("Platform")} <span className="highlight">{t("Modules")}</span>
                    </h2>
                    <p className="lp-section-subtitle">
                        {t("Three powerful categories designed to cover every aspect of your journey")}
                    </p>
                </div>

                {/* Sector */}
                {isSectorEnabled && (
                    <div className="lp-module-category lp-reveal" id="sector">
                        <div className="lp-module-category-inner">
                            <div className="lp-category-header">
                                <div className="lp-category-icon"><IconBarChart /></div>
                                <div>
                                    <h3 className="lp-category-title">{t("Sector")}</h3>
                                    <p className="lp-category-subtitle">{t("Industry-specific sustainability modules")}</p>
                                </div>
                            </div>
                            <div className="lp-module-cards">
                                {sectorModules.map((m) => (
                                    <div className="lp-module-card" key={m.title} onClick={() => navigate(m.route)}>
                                        <div className="lp-module-card-icon"><m.icon /></div>
                                        <h4>{t(m.title)}</h4>
                                        <p>{t(m.desc)}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Organization */}
                {isOrganizationEnabled && (
                    <div className="lp-module-category lp-reveal" id="organization">
                        <div className="lp-module-category-inner">
                            <div className="lp-category-header">
                                <div className="lp-category-icon"><IconBuilding /></div>
                                <div>
                                    <h3 className="lp-category-title">{t("Organization")}</h3>
                                    <p className="lp-category-subtitle">{t("Emissions, supply chain & compliance management")}</p>
                                </div>
                            </div>
                            <div className="lp-module-cards">
                                {organizationModules.map((m) => (
                                    <div className="lp-module-card" key={m.title} onClick={() => navigate(m.route)}>
                                        <div className="lp-module-card-icon"><m.icon /></div>
                                        <h4>{t(m.title)}</h4>
                                        <p>{t(m.desc)}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Engagement */}
                {isEngagementEnabled && (
                    <div className="lp-module-category lp-reveal" id="engagement">
                        <div className="lp-module-category-inner">
                            <div className="lp-category-header">
                                <div className="lp-category-icon"><IconHandshake /></div>
                                <div>
                                    <h3 className="lp-category-title">{t("Engagement")}</h3>
                                    <p className="lp-category-subtitle">{t("Supplier & B2B collaboration tools")}</p>
                                </div>
                            </div>
                            <div className="lp-module-cards">
                                {engagementModules.map((m) => (
                                    <div className="lp-module-card" key={m.title} onClick={() => navigate(m.route)}>
                                        <div className="lp-module-card-icon"><m.icon /></div>
                                        <h4>{t(m.title)}</h4>
                                        <p>{t(m.desc)}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
}

export default LandingPage;
